/**
 * Modal controller — the per-modal composition root.
 *
 * Composes the opted-in behaviour modules over one modal element and exposes a
 * clean `open` / `close` / `destroy`. It stores the `Unbind` of every module it
 * wired and fires them all on `destroy()` (idempotent). Per-open modules
 * (viewport-sync) are (re)bound on `show` and unbound on `hidden`, so listeners
 * never accumulate across open/close cycles.
 *
 * The controller is deliberately modal-AGNOSTIC (Open/Closed): it knows the
 * generic cross-cutting behaviours (history-back / viewport-sync / focus-trap)
 * but nothing about any specific modal. Feature-specific behaviour (e.g. the
 * model-finder's prefetch/render/destroy) is injected as `onShow/onShown/...`
 * hooks by the caller that constructs its own controller — the registry path
 * (modal-registry.ts) does NOT use those hooks; the MF feature subscribes to the
 * engine directly instead (see target §"Feature-hook injection").
 *
 * @see docs/architecture/modals/target-architecture.md §3.2.4
 */

import type { Unbind } from '../utils/unbind';
import { modalEngine, type ModalEngine } from './modal-engine';
import { bindModalViewportSync } from './modal-viewport-sync';
import { setupModalFocusTrap } from './modal-focus-trap';
import { setupModalHistoryBack } from './modal-history-back';

export interface ModalControllerConfig {
  historyBack: boolean;
  viewportSync: boolean;
  focusTrap: boolean;
  // Hooks let a caller-constructed controller inject feature behaviour without
  // the controller knowing about it (Open/Closed). The registry path leaves
  // these unset — feature code subscribes to the engine directly there.
  onShow?: (modal: HTMLElement) => void;
  onShown?: (modal: HTMLElement) => void;
  onHide?: (modal: HTMLElement) => void;
  onHidden?: (modal: HTMLElement) => void;
}

export class ModalController {
  private readonly modal: HTMLElement;
  private readonly engine: ModalEngine;
  /** Unbinds that live for the controller's whole lifetime (torn down on destroy). */
  private readonly unbinds: Unbind[] = [];
  /** Viewport-sync unbind — bound per open, cleared on hidden. */
  private viewportUnbind: Unbind | null = null;
  private destroyed = false;

  constructor(modal: HTMLElement, cfg: ModalControllerConfig, engine: ModalEngine = modalEngine) {
    this.modal = modal;
    this.engine = engine;

    if (cfg.focusTrap) {
      this.unbinds.push(setupModalFocusTrap(modal));
    }

    if (cfg.historyBack) {
      // History-back self-manages its lifecycle via the modal's own hidden event
      // (it does not return an Unbind — target §3.2.3 / G5). It routes its close +
      // lifecycle subscriptions through the SAME engine seam this controller uses,
      // so engine-probing stays confined to modal-engine.ts (DIP).
      setupModalHistoryBack(modal, this.engine);
    }

    if (cfg.viewportSync) {
      // Re-bind on each open, unbind on hidden so listeners don't accumulate.
      this.unbinds.push(
        this.engine.onShow(modal, () => {
          this.viewportUnbind?.();
          this.viewportUnbind = bindModalViewportSync(modal);
        })
      );
      this.unbinds.push(
        this.engine.onHidden(modal, () => {
          this.viewportUnbind?.();
          this.viewportUnbind = null;
        })
      );
    }

    // Feature hooks (caller-injected) — registered as independent engine listeners.
    if (cfg.onShow) this.unbinds.push(this.engine.onShow(modal, () => cfg.onShow!(modal)));
    if (cfg.onShown) this.unbinds.push(this.engine.onShown(modal, () => cfg.onShown!(modal)));
    if (cfg.onHide) this.unbinds.push(this.engine.onHide(modal, () => cfg.onHide!(modal)));
    if (cfg.onHidden) this.unbinds.push(this.engine.onHidden(modal, () => cfg.onHidden!(modal)));
  }

  /** The modal element this controller manages (read-only). Lets a caller that
   *  triggered a scoped registry re-scan (e.g. the foreign-modal upgrade adapter)
   *  identify which returned controller belongs to which node. */
  public get modalEl(): HTMLElement {
    return this.modal;
  }

  public open(): void {
    this.engine.open(this.modal);
  }

  public close(): void {
    this.engine.close(this.modal);
  }

  /** Fire every stored Unbind. Idempotent — safe to call more than once. */
  public destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.viewportUnbind?.();
    this.viewportUnbind = null;
    for (const unbind of this.unbinds.splice(0)) {
      unbind();
    }
  }
}
